import { z } from 'zod';
import { baseEntityFields, idSchema, isoDateTimeSchema } from './common.schema';
import { datapointNameSchema, sensorKeySchema } from './sensor.schema';
import { ALERT_SEVERITIES } from '../constants/severities';
import { DETECTION_MODES } from './alert.schema';
/**
 * Integration — per-tenant outbound channel configuration, discriminated
 * on `kind` (tech-arch §4.2). Secret VALUES are never stored in these
 * documents, and tenants never choose secret NAMES (#48):
 *  - the API accepts secret VALUES (write-only inputs: `serviceToken`,
 *    `apiKey`, `hmacSecret`, `secretHeaderValue`) and never returns them;
 *  - the server derives a tenant-scoped Secret Manager name for each, writes
 *    the value there, and stores only that SYSTEM-GENERATED name in the
 *    `*SecretName` fields (plus a `*Set` flag), which are not client-writable;
 *  - delivery resolves a secret only through a tenant-checked path
 *    (functions/src/delivery/secrets.ts).
 */
export const INTEGRATION_KINDS = ['desk', 'dispatch', 'webhook'];
/** A secret VALUE as accepted by the API: write-only, trimmed (a pasted token
 *  with a trailing newline must not break the auth header), never echoed. */
const secretValue = z.string().trim().min(1).max(8192);
const integrationCommonFields = {
    name: z.string().min(1).max(200),
    enabled: z.boolean().default(true),
};
/* ------------------------------------------------------------------ */
/* Desk ticket rules (#19)                                             */
/* ------------------------------------------------------------------ */
export const DESK_DELIVERY_INTENTS = ['hub', 'ticket'];
export const DESK_TICKET_RULE_LIMITS = { maxRules: 50, maxListItems: 50 };
const ruleList = (item) => z.array(item).min(1).max(DESK_TICKET_RULE_LIMITS.maxListItems).optional();
/**
 * Which alerts a rule selects. Fields AND together; the values inside one
 * field are alternatives (OR). At least one filter is required unless the rule
 * says `all: true` explicitly — "every alert becomes a ticket" must be a
 * deliberate choice, never what an empty rule happens to mean.
 */
export const deskTicketRuleMatchSchema = z
    .object({
    all: z.literal(true).optional(),
    sensorKeys: ruleList(sensorKeySchema),
    datapoints: ruleList(datapointNameSchema),
    severities: ruleList(z.enum(ALERT_SEVERITIES)),
    clientIds: ruleList(idSchema),
    resourceIds: ruleList(idSchema),
    /** The alert rule (routing) that routed the alert. */
    alertRuleIds: ruleList(idSchema),
    detectionModes: ruleList(z.enum(DETECTION_MODES)),
    /** `no-data` conditions vs ordinary threshold breaches. */
    conditionKinds: ruleList(z.enum(['threshold', 'no-data'])),
})
    .strict()
    .superRefine((val, ctx) => {
    const filters = [val.sensorKeys, val.datapoints, val.severities, val.clientIds, val.resourceIds, val.alertRuleIds, val.detectionModes, val.conditionKinds].filter((f) => f !== undefined).length;
    if (val.all === true && filters > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '"all" cannot be combined with filters' });
    }
    if (val.all !== true && filters === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'a ticket rule needs at least one filter, or all: true set explicitly',
        });
    }
});
export const deskTicketRuleSchema = z
    .object({
    /** Stable id chosen by the editor; unique within the integration. */
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
    name: z.string().trim().min(1).max(200),
    enabled: z.boolean().default(true),
    match: deskTicketRuleMatchSchema,
})
    .strict();
/** Alerts Hub is the default; a matching rule (first in order wins) sends an alert to Desk as a TICKET. */
export const deskTicketRuleListSchema = z
    .array(deskTicketRuleSchema)
    .max(DESK_TICKET_RULE_LIMITS.maxRules)
    .superRefine((rules, ctx) => {
    const seen = new Set();
    rules.forEach((r, i) => {
        if (seen.has(r.id))
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, 'id'], message: `duplicate ticket rule id "${r.id}"` });
        seen.add(r.id);
    });
});
const deskTicketRulesSchema = deskTicketRuleListSchema.default([]);
/** What a client sends for the Desk workspace: the slug or address the customer knows (the server resolves
 *  Desk's canonical id from it; clients can never set that id, #91). */
const deskWorkspaceInput = z.string().trim().min(1).max(128);
/** Non-secret Desk config, shared by the stored and input shapes. */
const deskFields = {
    ...integrationCommonFields,
    kind: z.literal('desk'),
    /** Sync policy: ack in Desk acks here; ticket resolution behavior. */
    ackSync: z.boolean().default(true),
    resolveOnTicketClose: z.boolean().default(false),
    /** Which alerts bypass the Alerts Hub and become Desk tickets (#19). Empty = everything goes to the hub. */
    ticketRules: deskTicketRulesSchema,
};
/** Desk Tier-2 module push into the existing Alerts Hub
 *  (desk-integration.md). STORED shape: `serviceTokenSecretName` is
 *  system-generated; `serviceTokenSet` says whether a value was ever stored. */
export const deskIntegrationConfigSchema = z
    .object({
    ...deskFields,
    /**
     * Desk's CANONICAL tenant id (its internal document key, e.g. `tenant-night-owl`). Server-managed and
     * server-side ONLY: used to address pushes, never returned by the API, never shown or edited (#91). The
     * customer-facing name is `deskWorkspace`.
     */
    deskTenantId: z.string().min(1).max(128),
    /**
     * What the customer recognizes and edits: the Desk workspace slug or address (#91). Optional only for
     * documents written before it existed; those read as their `deskTenantId`, which was the typed value.
     */
    deskWorkspace: z.string().min(1).max(128).optional(),
    /** System-generated Secret Manager name of the Desk service token. */
    serviceTokenSecretName: z.string().min(1).max(255),
    serviceTokenSet: z.boolean().default(false),
    /**
     * Desk verifies TWO independent credentials on the alert intake (#88): the module service token (X-Service-Token) and a
     * separate signing secret keyed into `X-Detect-Signature`, so the two rotate independently. The signing
     * secret is stored exactly like the token: a system-derived tenant secret name and a set flag, the value
     * write-only. Absent on integrations created before #88 (they push nothing until it is entered).
     */
    hmacSecretName: z.string().min(1).max(255).optional(),
    hmacSet: z.boolean().default(false),
    /**
     * Server-managed (never accepted from a client): has Desk ever accepted an authenticated push for this
     * workspace? A signup-time hint is only a typo guard, so it starts false and flips on the first push
     * Desk accepts with the service token. Changing `deskTenantId` resets it. (#84)
     */
    deskWorkspaceVerified: z.boolean().default(false),
    /**
     * Server-managed: which evidence produced the stored `deskTenantId` (#91). `typed` means the Platform
     * Directory could not match what was entered, so it is stored as typed (a push proves or refutes it).
     */
    deskTenantIdSource: z.enum(['directory-domain', 'directory-slug', 'typed']).default('typed'),
    /** Server-managed: the verified org domain that vouched for a `directory-domain` match (#109); null otherwise. */
    deskTenantIdDomain: z.string().min(1).max(253).nullable().optional(),
})
    .strict();
const dispatchFields = {
    ...integrationCommonFields,
    kind: z.literal('dispatch'),
    serviceProviderId: z.string().min(1).max(128),
    /** Auto-resolve the Detect alert when the Request completes (the
     *  metric remains the truth — a still-firing condition re-opens). */
    autoResolveOnCompletion: z.boolean().default(true),
};
/** Dispatch (Proteus) Request creation per its external-system pattern
 *  (dispatch-integration.md): X-API-Key + HMAC + external_metadata. STORED
 *  shape: both secret names are system-generated. */
export const dispatchIntegrationConfigSchema = z
    .object({
    ...dispatchFields,
    /** System-generated Secret Manager name of the ServiceProvider API key. */
    apiKeySecretName: z.string().min(1).max(255),
    /** System-generated Secret Manager name of the HMAC signing secret. */
    hmacSecretName: z.string().min(1).max(255),
    apiKeySet: z.boolean().default(false),
    hmacSet: z.boolean().default(false),
})
    .strict();
export const WEBHOOK_METHODS = ['POST', 'PUT'];
/** Non-secret webhook config, shared by the stored and input shapes. */
const webhookFields = {
    ...integrationCommonFields,
    kind: z.literal('webhook'),
    url: z
        .string()
        .url()
        .refine((u) => u.startsWith('https://'), 'webhook URLs must be https'),
    method: z.enum(WEBHOOK_METHODS).default('POST'),
    /** Static headers (no secrets — use secretHeaderName for auth). */
    headers: z.record(z.string().max(1024)).optional(),
    /** Header NAME for an auth value whose VALUE is stored as a secret. */
    secretHeaderName: z.string().min(1).max(128).optional(),
    /** JSON payload template with {{alert.*}} token substitution. */
    payloadTemplate: z.string().min(1).max(20000),
    /** Which alert lifecycle events fire this webhook. */
    events: z
        .array(z.enum(['raised', 'acknowledged', 'resolved', 'escalated']))
        .min(1)
        .default(['raised', 'resolved']),
};
/** Custom outbound webhook — the universal escape hatch (PRD §2.8 P0).
 *  Payload template supports `{{token}}` substitution from alert fields.
 *  STORED shape: `secretHeaderSecretName` is system-generated. */
export const webhookIntegrationConfigSchema = z
    .object({
    ...webhookFields,
    secretHeaderSecretName: z.string().min(1).max(255).optional(),
})
    .strict();
/**
 * Cross-field rules that can't live on the union members (zod 3
 * discriminatedUnion requires plain ZodObject members — no ZodEffects).
 * Applied to the input unions and the stored union.
 */
function webhookStoredPairingCheck(val, ctx) {
    if (val.kind === 'webhook') {
        const hasName = val.secretHeaderName !== undefined;
        const hasSecret = val.secretHeaderSecretName !== undefined;
        if (hasName !== hasSecret) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'secretHeaderName and its stored secret must be present together',
            });
        }
    }
}
/** CREATE: a header name needs its value, and a value needs its name. */
function webhookCreatePairingCheck(val, ctx) {
    if (val.kind === 'webhook') {
        if ((val.secretHeaderName !== undefined) !== (val.secretHeaderValue !== undefined)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'secretHeaderName and secretHeaderValue must be provided together',
            });
        }
    }
}
/** UPDATE: a value without a header name makes no sense (a name alone keeps
 *  the stored value; the server checks one exists). */
function webhookUpdatePairingCheck(val, ctx) {
    if (val.kind === 'webhook' && val.secretHeaderValue !== undefined && val.secretHeaderName === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'secretHeaderValue requires secretHeaderName',
        });
    }
}
/** CREATE input: secret VALUES are required where the channel needs them.
 *  Names are not accepted at all (strict) — the server generates them. */
export const integrationInputSchema = z
    .discriminatedUnion('kind', [
    z.object({ ...deskFields, deskWorkspace: deskWorkspaceInput, serviceToken: secretValue, hmacSecret: secretValue }).strict(),
    z.object({ ...dispatchFields, apiKey: secretValue, hmacSecret: secretValue }).strict(),
    z
        .object({ ...webhookFields, secretHeaderValue: secretValue.optional() })
        .strict(),
])
    .superRefine(webhookCreatePairingCheck);
/** UPDATE input: the non-secret config is re-submitted in full; every secret
 *  VALUE is optional — omit it to keep the stored one, provide it to rotate. */
export const integrationUpdateSchema = z
    .discriminatedUnion('kind', [
    // Omitting ticketRules on an update KEEPS the stored rules (the editor round-trips them; other
    // clients must not wipe them by not knowing about them); send [] to clear.
    z.object({ ...deskFields, deskWorkspace: deskWorkspaceInput, ticketRules: deskTicketRuleListSchema.optional(), serviceToken: secretValue.optional(), hmacSecret: secretValue.optional() }).strict(),
    z
        .object({ ...dispatchFields, apiKey: secretValue.optional(), hmacSecret: secretValue.optional() })
        .strict(),
    z
        .object({ ...webhookFields, secretHeaderValue: secretValue.optional() })
        .strict(),
])
    .superRefine(webhookUpdatePairingCheck);
/**
 * Stored integration = per-kind config extended with base entity fields.
 * NOT an intersection: intersecting the strict input union with the base
 * fields would reject valid stored docs (base fields are unknown keys to
 * the strict members). `.extend()` preserves strictness per member.
 */
/** Server-managed (#80): set once a deleted integration's secrets have been removed. Never a client input. */
const storedLifecycleFields = {
    ...baseEntityFields,
    secretsPurgedAt: isoDateTimeSchema.optional(),
    /** Server-managed (#97): while a delete's secret purge is running, until when a re-create must wait. */
    purgeLeaseUntil: isoDateTimeSchema.nullable().optional(),
    /** Who holds that lease (a per-request token), so two purges never run at once. */
    purgeLeaseOwner: z.string().min(1).max(64).nullable().optional(),
    /** Server-managed (#105): held by the ONE create/update that may write this integration's secrets right now. */
    writeLeaseUntil: isoDateTimeSchema.nullable().optional(),
    writeLeaseOwner: z.string().min(1).max(64).nullable().optional(),
    /** A create/update that lost to the delete wrote secrets while the purge held the lease: not purged for sure. */
    purgeDirtyAt: isoDateTimeSchema.nullable().optional(),
};
export const integrationSchema = z
    .discriminatedUnion('kind', [
    deskIntegrationConfigSchema.extend(storedLifecycleFields),
    dispatchIntegrationConfigSchema.extend(storedLifecycleFields),
    webhookIntegrationConfigSchema.extend(storedLifecycleFields),
])
    .superRefine(webhookStoredPairingCheck);
/** Reference to an integration used in delivery routing. */
export const integrationRefSchema = z
    .object({
    integrationId: idSchema,
    kind: z.enum(INTEGRATION_KINDS),
})
    .strict();
//# sourceMappingURL=integration.schema.js.map